<script setup>
const { id } = useRoute().params

const { data: blog, error: blogError } = await useFetch(`/api/blog/${id}`)

if (!blog.value || blogError.value) {
  throw createError({ statusCode: 404, statusMessage: 'Blog tidak ditemukan' })
}

const title = ref(blog.value.judul)
const description = ref(blog.value.meta_desc)

const affiliateAds = computed(() => ({
  mid: blog.value?.affiliate_mid ?? [],
  end: blog.value?.affiliate_end ?? [],
}))

// Split blog_content HTML at the nearest </p> to the midpoint
const blogContentHalves = computed(() => {
  const content = blog.value?.blog_content ?? ''
  if (!content) return ['', '']
  const mid = Math.floor(content.length / 2)
  const splitIdx = content.indexOf('</p>', mid)
  if (splitIdx === -1) return [content, '']
  const cutAt = splitIdx + 4
  return [content.slice(0, cutAt), content.slice(cutAt)]
})

const { data: blogs } = await useFetch('/api/blogs', { query: { limit: 4 } })

useHead({
  title,
  meta: [{ name: "description", content: description }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: blog.value.judul,
        description: blog.value.meta_desc,
        image: blog.value.image,
        author: {
          "@type": "Person",
          name: "Hitung Harga Jual Team",
        },
        publisher: {
          "@type": "Organization",
          name: "Hitung Harga Jual",
          logo: {
            "@type": "ImageObject",
            url: "https://www.hitung-harga-jual.com/logo.png",
          },
        },
        datePublished: blog.value.created_at,
        dateModified: blog.value.updated_at || blog.value.created_at,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://www.hitung-harga-jual.com/blogs/${id}`,
        },
        keywords:
          blog.value.keywords ||
          "GoFood, GrabFood, ShopeeFood, komisi food delivery, bisnis makanan online, kalkulasi harga",
        inLanguage: "id-ID",
      }),
    },
  ],
})
</script>

<template>
  <div>
    <div>
      <div v-if="blog">
        <div
          class="min-h-screen w-[90%] md:w-5/6 mx-auto flex flex-col sm:flex-row sm:space-x-10"
        >
          <div class="flex-1 min-w-0 py-4">
            <!-- breadcrumb -->
            <div
              class="mb-4 text-xs sm:text-base flex space-x-1 py-2 text-md font-medium text-gray-400"
            >
              <div>Home</div>
              <div>•</div>
              <div>
                <NuxtLink to="/blogs">Blog</NuxtLink>
              </div>
              <div>•</div>
              <div>{{ blog.judul }}</div>
            </div>
            <div class="w-full rounded-lg overflow-hidden">
              <img :src="blog.image" />
            </div>
            <section id="blog_content" class="mt-6">
              <div
                class="mt-4 flex justify-between border-t border-b border-slate-300 py-2 my-4 text-md font-medium text-gray-400"
              >
                <div>{{ new Date(blog.created_at).toLocaleDateString('id-ID', {year: 'numeric', month: 'long', day: 'numeric'}) }}</div>
                <div>by Admin</div>
              </div>
              <header class="py-2 mb-2">
                <h1
                  id="blog_title"
                  class="md:text-4xl text-gray-700 font-bold mb-2"
                >
                  {{ blog.judul }}
                </h1>
                <div
                  id="blog_short_desc"
                  class="text-md text-gray-500 font-medium mt-4"
                >
                  {{ blog.short_desc }}
                </div>
              </header>
                <hr class="border-t-2 border-gray-300" />
              <div class="mt-8 blog-content" v-html="blogContentHalves[0]"></div>
              <AffiliateAd :links="affiliateAds?.mid ?? []" position="mid" />
              <div class="blog-content" v-html="blogContentHalves[1]"></div>
            </section>
            <AffiliateAd :links="affiliateAds?.end ?? []" position="end" />
          </div>
          <!-- Right leg -->
          <div class="w-full sm:w-1/4 px-2 pt-2">
            <h2>Post Terkait</h2>
            <!-- Search -->
            <div class="">
              <input
                class="w-full rounded-lg border-none bg-slate-200 p-2"
                type="text"
                placeholder="Cari"
              />
            </div>
            <div class="mt-10">
              <div class="grid grid-cols-1 md:grid-rows-4 gap-4 my-8">
                <div v-for="relatedBlog in blogs" :key="relatedBlog.id">
                  <NuxtLink :to="`/blogs/${relatedBlog.id}`">
                    <BlogCard :blog="relatedBlog" />
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.blog-content :deep(table) {
  display: block;
  overflow-x: auto;
  max-width: 100%;
}
</style>
